import React, { useState, ChangeEvent, FormEvent } from "react";
import { FaRegCircleCheck } from "react-icons/fa6";
import strings from "../../constants/strings";
import logo from "../../assets/images/logo.png";

interface FormData {
  name: string;
  email: string;
  title: string;
  comment: string;
}

const ContactUsScreen = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    title: "",
    comment: ""
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const [generalBanner, setGeneralBanner] = useState("");
  const [isError, setIsError] = useState(true);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    setErrors({ ...errors, [e.target.id]: "" });
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = strings.enterName;
    }

    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = strings.invalidEmail;
    }

    if (!formData.title.trim()) {
      newErrors.title = strings.enterTitle;
    }

    if (!formData.comment.trim()) {
      newErrors.comment = strings.enterComment;
    }

    setErrors(newErrors);

    const errorCount = Object.keys(newErrors).length;
    if (errorCount > 0) {
      setGeneralBanner(`${strings.fixAllErrors} ${errorCount}`);
      setIsError(true);
      return false;
    }

    setGeneralBanner("");
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await fetch("https://dev-admin.ptlytics.ca/api/website-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer ChMHiRGMOS0sgpuB9EJIdNBGIl92llR8bn9sxtZ1Eykm4IxHJlI8yTOq6jP7O6Y1XjHqk1GsuRE9tsTdIhUCAZXJUcpjDKY6NohBvaWwwA0azRqu2nYrWLIiBCh20Y6V"
        },
        body: JSON.stringify(formData)
      });
      if (!response.ok) {
        setGeneralBanner(strings.errorEmail);
        setIsError(true);
      } else {
        setGeneralBanner(strings.emailSent);
        setIsError(false);
        setFormData({
          name: "",
          email: "",
          title: "",
          comment: ""
        });

        setTimeout(() => {
          setGeneralBanner("");
        }, 3000);
      }
    } catch (error) {
      setGeneralBanner(`${strings.errorSubmitting} ${error}`);
      setIsError(true);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-home-background bg-cover bg-center grid grid-cols-1 lg:grid-cols-2 lg:gap-x-6 scrollable-container">
      <div className="flex flex-col items-center lg:items-start justify-center text-white pt-10 px-10 lg:pl-48 lg:pr-20">
        <img src={logo} alt="PTLytics Logo" className="w-[193px] h-[60px] mb-4" />
        <p className="text-[22px] lg:text-[64px] text-center lg:text-start font-urbanist font-bold">
          {strings.bannerContactUs}
        </p>
      </div>
      <div className="flex items-center justify-center lg:justify-start p-6 min-h-screen scrollable-container">
        <div className="bg-white rounded-md shadow-md p-8 w-full max-w-[488px] h-auto">
          <h2 className="text-[24px] lg:text-[28px] font-semibold mb-2 font-urbanist">
            {strings.contactUs}
          </h2>
          <p className="text-custom-gray mb-6 font-normal">
            {strings.contactUsDescription}
          </p>

          {generalBanner && (
            <div
              className={`px-4 py-3 mb-4 border-b-2 ${isError
                ? "bg-custom-light-red border-custom-dark-red text-custom-dark-red"
                : "bg-custom-light-green border-custom-dark-green text-custom-dark-green"
                }`}
            >
              <strong className="flex">
                {isError ? (
                  <span className="mr-2 font-bold text-2xl self-start">&#9888;</span>
                ) : (
                  <FaRegCircleCheck className="mr-2 text-custom-dark-green self-center font-bold" />
                )}
                <span className="self-center">{generalBanner}</span>
              </strong>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className={`border ${errors.name ? 'border-custom-dark-red border-2 mb-2' : 'border-gray-300 mb-4'} rounded-md p-3`}>
              <label htmlFor="name" className="block text-custom-gray font-normal text-sm mb-1">
                {strings.nameMandatory}
              </label>
              <input
                id="name"
                type="text"
                placeholder={strings.namePlaceholder}
                className="w-full border-none outline-none placeholder:text-custom-gray text-base"
                value={formData.name}
                onChange={handleChange}
              />
            </div>
            {errors.name && (
              <p className="text-custom-red text-sm mt-1 flex items-center">
                <span className="mr-1 font-bold text-xl">&#9888;</span> {errors.name}
              </p>
            )}

            <div className={`border ${errors.email ? 'border-custom-dark-red border-2 my-2' : 'border-gray-300 my-4'} rounded-md p-3`}>
              <label htmlFor="email" className="block text-custom-gray font-normal text-sm mb-1">
                {strings.emailMandatory}
              </label>
              <input
                id="email"
                type="email"
                placeholder={strings.emailPlaceholder}
                className="w-full border-none outline-none placeholder:text-custom-gray text-base"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            {errors.email && (
              <p className="text-custom-red text-sm mt-1 flex items-center">
                <span className="mr-1 font-bold text-xl">&#9888;</span> {errors.email}
              </p>
            )}

            <div className={`border ${errors.title ? 'border-custom-dark-red border-2 my-2' : 'border-gray-300 my-4'} rounded-md p-3`}>
              <label htmlFor="title" className="block text-custom-gray font-normal text-sm mb-1">
                {strings.titleMandatory}
              </label>
              <input
                id="title"
                type="text"
                placeholder={strings.addDescription}
                className="w-full border-none outline-none placeholder:text-custom-gray text-base"
                value={formData.title}
                onChange={handleChange}
              />
            </div>
            {errors.title && (
              <p className="text-custom-red text-sm mt-1 flex items-center">
                <span className="mr-1 font-bold text-xl">&#9888;</span> {errors.title}
              </p>
            )}

            <div className={`border ${errors.comment ? 'border-custom-dark-red border-2 my-2' : 'border-gray-300 my-4'} rounded-md p-3`}>
              <label htmlFor="comment" className="block text-custom-gray font-normal text-sm mb-1">
                {strings.commentMandatory}
              </label>
              <textarea
                id="comment"
                placeholder={strings.shareQuestion}
                className="w-full border-none outline-none placeholder:text-custom-gray text-base"
                rows={4}
                value={formData.comment}
                onChange={handleChange}
              />
            </div>
            {errors.comment && (
              <p className="text-custom-red text-sm mt-1 flex items-center mb-2">
                <span className="mr-1 font-bold text-xl">&#9888;</span> {errors.comment}
              </p>
            )}

            <button
              type="submit"
              className="bg-custom-blue text-white px-4 py-2 rounded-full w-full font-bold"
            >
              {strings.submitBtn}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactUsScreen;
